import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Dialog, DialogContent } from './ui/dialog';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Gallery = () => {
  const { t } = useLanguage();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API}/gallery`);
        const data = response.data.images || [];
        // Normalize: each item may be a string (legacy) or object with url/id
        const normalized = data.map(item => {
          if (typeof item === 'string') return { url: item, alt_text: 'Madrid Tuk Tuk Tour' };
          // For DB images, resolve URL
          let url = item.url || '';
          if (url.startsWith('/api/')) url = `${process.env.REACT_APP_BACKEND_URL}${url}`;
          return { ...item, url };
        });
        setImages(normalized);
      } catch (error) {
        console.error('Error fetching gallery:', error);
        setImages([
          "https://images.unsplash.com/photo-1539037116277-4db20889f2d4?w=600&q=80",
          "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80",
          "https://images.unsplash.com/photo-1543783207-ec64e4d95325?w=600&q=80",
          "https://images.unsplash.com/photo-1559563362-c667ba5f5480?w=600&q=80",
          "https://images.unsplash.com/photo-1578307985320-34b61a66c195?w=600&q=80",
          "https://images.unsplash.com/photo-1579282240050-352db0a14c21?w=600&q=80"
        ].map(url => ({ url, alt_text: 'Madrid Tuk Tuk Tour' })));
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const getUrl = (image) => {
    if (typeof image === 'string') return image;
    return image.url || '';
  };

  const getAlt = (image, index) => {
    if (typeof image === 'string') return `Tour momento ${index + 1}`;
    return image.alt_text || image.title || `Tour momento ${index + 1}`;
  };

  const getFullUrl = (image) => {
    const url = getUrl(image);
    // For unsplash images, upgrade quality
    if (url.includes('unsplash.com')) return url.replace('w=600', 'w=1200');
    return url;
  };

  return (
    <section 
      id="gallery"
      className="py-24 md:py-32 bg-[#FDFBF7]"
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-[#C2410C] mb-4 font-semibold">
            Fotos
          </p>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#1C1917] mb-4">
            {t.gallery.title}
          </h2>
          <p className="text-lg text-[#57534E]">
            {t.gallery.subtitle}
          </p>
        </motion.div>

        {/* Gallery Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div 
                key={i} 
                className={`bg-[#E7E5E4] rounded-xl animate-pulse ${
                  i === 1 || i === 5 ? 'row-span-2 aspect-[3/4]' : 'aspect-square'
                }`} 
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((image, index) => (
              <motion.div
                key={typeof image === 'string' ? index : (image.id || index)}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className={`relative rounded-xl overflow-hidden cursor-pointer img-zoom ${
                  index === 0 || index === 4 ? 'row-span-2' : ''
                }`}
                onClick={() => setSelectedImage(image)}
                data-testid={`gallery-image-${index}`}
              >
                <img
                  src={getUrl(image)}
                  alt={getAlt(image, index)}
                  className={`w-full h-full object-cover ${
                    index === 0 || index === 4 ? 'aspect-[3/4]' : 'aspect-square'
                  }`}
                />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/0 hover:bg-black/30 transition-colors flex items-center justify-center">
                  <span className="text-white opacity-0 hover:opacity-100 transition-opacity font-semibold">
                    Ver
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl p-0 bg-transparent border-none">
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 z-10 p-2 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
            data-testid="gallery-close-btn"
          >
            <X className="h-6 w-6" />
          </button>
          {selectedImage && (
            <img
              src={getFullUrl(selectedImage)}
              alt={getAlt(selectedImage, 0)}
              className="w-full h-auto rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Gallery;
